import { c as _c } from "react/compiler-runtime";
import { ExternalLink } from 'lucide-react';
import { memo, type ReactElement, type ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { InlineCitation, InlineCitationCard, InlineCitationCardBody, InlineCitationCardTrigger, InlineCitationCarousel, InlineCitationCarouselContent, InlineCitationCarouselHeader, InlineCitationCarouselIndex, InlineCitationCarouselItem, InlineCitationCarouselNext, InlineCitationCarouselPrev, InlineCitationSource, InlineCitationText } from '../../ai-elements';
import { createComponents } from '../../ui/markdown-components';
import { getSourceMetadata, type SourceUrlPart, type SourcesMap } from '../lib/sources';
interface MarkdownWithCitationsProps {
  children: string;
  sourcesMap: SourcesMap;
  messageUuid?: string;
  className?: string;
}

// Pattern to match [SOURCE-<id>] where <id> is any non-bracket characters
const CITATION_PATTERN = /\[SOURCE-([^\]]+)\]/g;

// Pattern to match reference definitions: [SOURCE-x]: url
const REFERENCE_DEFINITION_PATTERN = /^\[SOURCE-([^\]]+)\]:\s*(.+)$/gm;

// Pattern to match reference-style links: [text][SOURCE-x]
const REFERENCE_LINK_PATTERN = /\[([^\]]+)\]\[SOURCE-([^\]]+)\]/g;

// Pattern to check if text is a SOURCE reference itself
const SOURCE_TEXT_PATTERN = /^SOURCE-([^\]]+)$/;

/**
 * Pre-processes markdown to convert reference-style citation links to inline format.
 * Transforms [text][SOURCE-x] with [SOURCE-x]: url definitions into [text](url) [SOURCE-x]
 * so our existing citation pattern can handle them.
 *
 * Special case: if the link text is also a SOURCE-x pattern (e.g., [SOURCE-6][SOURCE-7]),
 * both are treated as adjacent citations, not as a link.
 */
function preprocessMarkdownCitations(markdown: string): string {
  // Extract reference definitions and build a map
  const definitions = new Map<string, string>();
  Array.from(markdown.matchAll(REFERENCE_DEFINITION_PATTERN)).forEach(match => {
    const sourceId = match[1];
    const url = match[2].trim();
    definitions.set(sourceId, url);
  });

  // If no SOURCE definitions found, return as-is
  if (definitions.size === 0) {
    return markdown;
  }

  // Replace reference-style links with inline links + citation marker
  let processed = markdown.replace(REFERENCE_LINK_PATTERN, (_fullMatch, linkText, sourceId) => {
    // Check if the link text is itself a SOURCE reference
    const linkTextSourceMatch = SOURCE_TEXT_PATTERN.exec(linkText);
    if (linkTextSourceMatch) {
      // Both are citations - output them as separate citation markers with space between
      return `[SOURCE-${linkTextSourceMatch[1]}] [SOURCE-${sourceId}]`;
    }
    const url = definitions.get(sourceId);
    if (url) {
      // Convert to inline link followed by citation marker
      return `[${linkText}](${url}) [SOURCE-${sourceId}]`;
    }
    // If no definition found, keep original
    return _fullMatch;
  });

  // Remove the reference definitions from the end of the markdown
  processed = processed.replace(REFERENCE_DEFINITION_PATTERN, '');

  // Clean up any trailing whitespace from removed definitions
  processed = processed.trimEnd();
  return processed;
}

/**
 * Renders a single inline citation with hover card
 */
interface CitationProps {
  source: SourceUrlPart;
  index: number;
  citationKey: string;
}
function Citation(t0) {
  const $ = _c(60);
  const {
    source,
    index,
    citationKey
  } = t0;
  let T0;
  let T1;
  let T2;
  let T3;
  let T4;
  let T5;
  let T6;
  let metadata;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== citationKey || $[1] !== index || $[2] !== source) {
    metadata = getSourceMetadata(source);
    T6 = InlineCitation;
    t7 = citationKey;
    if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <InlineCitationText />;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    T5 = InlineCitationCard;
    let t9;
    if ($[20] !== source.url) {
      t9 = [source.url];
      $[20] = source.url;
      $[21] = t9;
    } else {
      t9 = $[21];
    }
    if ($[22] !== index || $[23] !== t9) {
      t6 = <InlineCitationCardTrigger sources={t9}>{index}</InlineCitationCardTrigger>;
      $[22] = index;
      $[23] = t9;
      $[24] = t6;
    } else {
      t6 = $[24];
    }
    T4 = InlineCitationCardBody;
    T3 = InlineCitationCarousel;
    if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <InlineCitationCarouselHeader><InlineCitationCarouselPrev /><InlineCitationCarouselIndex /><InlineCitationCarouselNext /></InlineCitationCarouselHeader>;
      $[25] = t5;
    } else {
      t5 = $[25];
    }
    T2 = InlineCitationCarouselContent;
    T1 = InlineCitationCarouselItem;
    T0 = InlineCitationSource;
    t1 = source.title;
    t2 = metadata?.textSnippet;
    let t10;
    if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <ExternalLink className="h-4 w-4" />;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    if ($[27] !== source.url) {
      t3 = <a href={source.url} target="_blank" rel="noopener noreferrer" className="text-muted-foreground hover:text-primary absolute top-0 right-0 transition-colors" onClick={_temp}>{t10}</a>;
      $[27] = source.url;
      $[28] = t3;
    } else {
      t3 = $[28];
    }
    t4 = metadata?.fileDate && <div className="text-muted-foreground text-xs">Date: {new Date(metadata.fileDate).toLocaleDateString()}</div>;
    $[0] = citationKey;
    $[1] = index;
    $[2] = source;
    $[3] = T0;
    $[4] = T1;
    $[5] = T2;
    $[6] = T3;
    $[7] = T4;
    $[8] = T5;
    $[9] = T6;
    $[10] = metadata;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    T0 = $[3];
    T1 = $[4];
    T2 = $[5];
    T3 = $[6];
    T4 = $[7];
    T5 = $[8];
    T6 = $[9];
    metadata = $[10];
    t1 = $[11];
    t2 = $[12];
    t3 = $[13];
    t4 = $[14];
    t5 = $[15];
    t6 = $[16];
    t7 = $[17];
    t8 = $[18];
  }
  let t9;
  if ($[29] !== metadata) {
    t9 = metadata?.emailFrom && <div className="text-muted-foreground text-xs">From: {metadata.emailFrom}</div>;
    $[29] = metadata;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  let t10;
  if ($[31] !== T0 || $[32] !== t1 || $[33] !== t2 || $[34] !== t3 || $[35] !== t4 || $[36] !== t9) {
    t10 = <T0 title={t1} description={t2}>{t3}{t4}{t9}</T0>;
    $[31] = T0;
    $[32] = t1;
    $[33] = t2;
    $[34] = t3;
    $[35] = t4;
    $[36] = t9;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  let t11;
  if ($[38] !== T1 || $[39] !== t10) {
    t11 = <T1>{t10}</T1>;
    $[38] = T1;
    $[39] = t10;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  let t12;
  if ($[41] !== T2 || $[42] !== t11) {
    t12 = <T2>{t11}</T2>;
    $[41] = T2;
    $[42] = t11;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  let t13;
  if ($[44] !== T3 || $[45] !== t12 || $[46] !== t5) {
    t13 = <T3>{t5}{t12}</T3>;
    $[44] = T3;
    $[45] = t12;
    $[46] = t5;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== T4 || $[49] !== t13) {
    t14 = <T4>{t13}</T4>;
    $[48] = T4;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  let t15;
  if ($[51] !== T5 || $[52] !== t14 || $[53] !== t6) {
    t15 = <T5>{t6}{t14}</T5>;
    $[51] = T5;
    $[52] = t14;
    $[53] = t6;
    $[54] = t15;
  } else {
    t15 = $[54];
  }
  let t16;
  if ($[55] !== T6 || $[56] !== t15 || $[57] !== t7 || $[58] !== t8) {
    t16 = <T6 key={t7}>{t8}{t15}</T6>;
    $[55] = T6;
    $[56] = t15;
    $[57] = t7;
    $[58] = t8;
    $[59] = t16;
  } else {
    t16 = $[59];
  }
  return t16;
}

/**
 * Parse text and replace [SOURCE-<id>] markers with citation components.
 * Citation numbers are determined by the source's position in the sourceIndexMap (1-based).
 */
function _temp(e) {
  return e.stopPropagation();
}
function parseTextWithCitations(text: string, sourcesMap: SourcesMap, sourceIndexMap: Map<string, number>): (string | ReactElement)[] {
  const parts: (string | ReactElement)[] = [];
  let lastIndex = 0;
  let citationIndex = 0;

  // Reset lastIndex to ensure regex starts from beginning
  CITATION_PATTERN.lastIndex = 0;
  let match = CITATION_PATTERN.exec(text);
  while (match !== null) {
    const matchIndex = match.index;
    const sourceId = match[1];

    // Add text before the citation
    if (matchIndex > lastIndex) {
      parts.push(text.substring(lastIndex, matchIndex));
    }

    // Get the source for this citation
    const source = sourcesMap.get(sourceId);
    if (source) {
      // Get the 1-based index for this source
      const sourceIndex = sourceIndexMap.get(sourceId) ?? 0;
      const currentCitationIndex = citationIndex;
      citationIndex += 1;
      parts.push(<Citation key={`citation-${sourceId}-${currentCitationIndex}`} source={source} index={sourceIndex} citationKey={`citation-${sourceId}-${currentCitationIndex}`} />);
    } else {
      // If source not found, just keep the marker
      parts.push(match[0]);
    }
    lastIndex = CITATION_PATTERN.lastIndex;
    match = CITATION_PATTERN.exec(text);
  }

  // Add remaining text after the last citation
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }
  return parts;
}

/**
 * Recursively process children to find and replace text nodes containing citations
 */
function processChildren(children: ReactNode, sourcesMap: SourcesMap, sourceIndexMap: Map<string, number>): ReactNode {
  if (typeof children === 'string') {
    return parseTextWithCitations(children, sourcesMap, sourceIndexMap);
  }
  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        const parsed = parseTextWithCitations(child, sourcesMap, sourceIndexMap);
        // eslint-disable-next-line react/no-array-index-key
        return parsed.length === 1 ? parsed[0] : <span key={index}>{parsed}</span>;
      }
      return child;
    });
  }
  return children;
}

/**
 * Create markdown components with citation support
 */
function createCitationComponents(sourcesMap: SourcesMap, sourceIndexMap: Map<string, number>, messageUuid?: string): Partial<Components> {
  const baseComponents = createComponents(messageUuid);
  return {
    ...baseComponents,
    // Override the paragraph component to handle citations in text
    p: ({
      children: pChildren,
      ...props
    }) => {
      const processedChildren = processChildren(pChildren, sourcesMap, sourceIndexMap);
      return <p className="my-4" {...props}>
          {processedChildren}
        </p>;
    },
    // Also handle text in other common elements
    li: ({
      children: liChildren,
      ...props
    }) => {
      const processedChildren = processChildren(liChildren, sourcesMap, sourceIndexMap);
      return <li className="py-1" {...props}>
          {processedChildren}
        </li>;
    },
    strong: ({
      children: strongChildren,
      ...props
    }) => {
      const processedChildren = processChildren(strongChildren, sourcesMap, sourceIndexMap);
      return <span className="font-semibold" {...props}>
          {processedChildren}
        </span>;
    },
    em: ({
      children: emChildren,
      ...props
    }) => {
      const processedChildren = processChildren(emChildren, sourcesMap, sourceIndexMap);
      return <em {...props}>{processedChildren}</em>;
    }
  };
}
const remarkPlugins = [remarkGfm];
function NonMemoizedMarkdownWithCitations({
  children,
  sourcesMap,
  messageUuid,
  className
}: MarkdownWithCitationsProps) {
  // Pre-process markdown to convert reference-style citation links
  const processedMarkdown = useMemo(() => preprocessMarkdownCitations(children), [children]);

  // Create sources array (preserves insertion order)
  const sourcesArray = useMemo(() => Array.from(sourcesMap.values()), [sourcesMap]);

  // Create sourceId -> 1-based index map for O(1) lookup during citation parsing
  const sourceIndexMap = useMemo(() => {
    const map = new Map<string, number>();
    sourcesArray.forEach((source, index) => {
      map.set(source.sourceId, index + 1);
    });
    return map;
  }, [sourcesArray]);

  // Memoize components object to avoid recreation on every render
  const components = useMemo<Partial<Components>>(() => createCitationComponents(sourcesMap, sourceIndexMap, messageUuid), [sourcesMap, sourceIndexMap, messageUuid]);
  return <ReactMarkdown remarkPlugins={remarkPlugins} components={components} className={className}>
      {processedMarkdown}
    </ReactMarkdown>;
}
function areSourcesMapsEqual(prev: SourcesMap, next: SourcesMap): boolean {
  if (prev === next) {
    return true;
  }
  if (prev.size !== next.size) {
    return false;
  }
  return Array.from(prev.entries()).every(([sourceId, prevSource]) => {
    const nextSource = next.get(sourceId);
    if (!nextSource) {
      return false;
    }
    if (prevSource.url !== nextSource.url || prevSource.title !== nextSource.title) {
      return false;
    }
    const prevMetadata = getSourceMetadata(prevSource);
    const nextMetadata = getSourceMetadata(nextSource);
    return prevMetadata?.textSnippet === nextMetadata?.textSnippet && prevMetadata?.fileDate === nextMetadata?.fileDate && prevMetadata?.emailFrom === nextMetadata?.emailFrom;
  });
}
export const MarkdownWithCitations = memo(NonMemoizedMarkdownWithCitations, (prevProps, nextProps) => prevProps.children === nextProps.children && areSourcesMapsEqual(prevProps.sourcesMap, nextProps.sourcesMap) && prevProps.messageUuid === nextProps.messageUuid && prevProps.className === nextProps.className);